"use client";

import { useMemo, useCallback } from "react";
import { useMutation, useQuery } from "convex/react";
import { useUser } from "@clerk/nextjs";

import { api } from "@/convex/_generated/api";
import type { Doc, Id } from "@/convex/_generated/dataModel";

const EXPENSE_ICON_KEYS = [
  "shopping-bag",
  "bolt",
  "bus",
  "clapperboard",
  "home",
  "credit-card",
  "flame",
  "wallet",
];

const SAVING_ICON_KEYS = ["piggy-bank", "wallet", "credit-card", "safe"];
const INVEST_ICON_KEYS = ["line-chart", "chart-line", "banknote", "maple"];
const GOAL_ICON_KEYS = ["trophy", "target", "flag", "rocket", "gift", "piggy-bank", "star", "gem"];

export const expenseCategories = [
  "Groceries",
  "Utilities",
  "Transport",
  "Entertainment",
  "Bill",
  "Savings",
  "Misc",
  "Other",
];

export type ExpenseRecord = Doc<"expenses">;
export type SavingsRecord = Doc<"savings">;
export type InvestmentRecord = Doc<"investments">;
export type GoalRecord = Doc<"savingsGoals">;

function pickRandom<T>(items: T[]): T {
  return items[Math.floor(Math.random() * items.length)];
}

function toMonthLabel(timestamp: number) {
  return new Date(timestamp).toLocaleDateString("en-US", {
    month: "short",
    year: "2-digit",
  });
}

function formatDisplayName(raw?: string | null) {
  if (!raw) return "there";
  return raw.trim();
}

export function formatCurrency(amount: number) {
  return new Intl.NumberFormat("en-US", {
    style: "currency",
    currency: "USD",
    minimumFractionDigits: 2,
    maximumFractionDigits: 2,
  }).format(amount);
}

export function useBudgetData() {
  const { user } = useUser();

  const expensesQuery = useQuery(api.records.listExpenses, {});
  const savingsQuery = useQuery(api.records.listSavings, {});
  const investmentsQuery = useQuery(api.records.listInvestments, {});
  const goalsQuery = useQuery(api.records.listGoals, {});

  const expenses = useMemo(() => expensesQuery ?? [], [expensesQuery]);
  const savings = useMemo(() => savingsQuery ?? [], [savingsQuery]);
  const investments = useMemo(() => investmentsQuery ?? [], [investmentsQuery]);
  const goals = useMemo(() => goalsQuery ?? [], [goalsQuery]);

  const addExpense = useMutation(api.records.addExpense);
  const deleteExpense = useMutation(api.records.deleteExpense);
  const addSaving = useMutation(api.records.addSaving);
  const deleteSaving = useMutation(api.records.deleteSaving);
  const addInvestment = useMutation(api.records.addInvestment);
  const deleteInvestment = useMutation(api.records.deleteInvestment);
  const addGoal = useMutation(api.records.addGoal);
  const deleteGoal = useMutation(api.records.deleteGoal);
  const incrementGoal = useMutation(api.records.incrementGoal);

  const totalSavings = useMemo(
    () => savings.reduce((sum, record) => sum + Number(record.amount), 0),
    [savings]
  );
  const totalInvestments = useMemo(
    () => investments.reduce((sum, record) => sum + Number(record.amount), 0),
    [investments]
  );
  const totalExpenses = useMemo(
    () => expenses.reduce((sum, record) => sum + Number(record.amount), 0),
    [expenses]
  );
  const totalGoalTarget = useMemo(
    () => goals.reduce((sum, record) => sum + Number(record.targetAmount), 0),
    [goals]
  );
  const totalGoalSaved = useMemo(
    () => goals.reduce((sum, record) => sum + Number(record.currentAmount), 0),
    [goals]
  );

  const thisMonthKey = new Date().toISOString().slice(0, 7);

  const monthlyExpenseTotal = useMemo(
    () =>
      expenses
        .filter((record) => new Date(record.createdAt).toISOString().slice(0, 7) === thisMonthKey)
        .reduce((sum, record) => sum + Number(record.amount), 0),
    [expenses, thisMonthKey]
  );

  const monthlySavingsTotal = useMemo(
    () =>
      savings
        .filter((record) => new Date(record.createdAt).toISOString().slice(0, 7) === thisMonthKey)
        .reduce((sum, record) => sum + Number(record.amount), 0),
    [savings, thisMonthKey]
  );

  const expenseByCategory = useMemo(() => {
    const grouped = expenses.reduce<Record<string, number>>((acc, record) => {
      acc[record.category] = (acc[record.category] ?? 0) + Number(record.amount);
      return acc;
    }, {});

    return Object.entries(grouped).map(([name, value]) => ({ name, value }));
  }, [expenses]);

  const savingsTimeline = useMemo(() => {
    const grouped = savings.reduce<Record<string, number>>((acc, record) => {
      const key = new Date(record.createdAt).toISOString().slice(0, 7);
      acc[key] = (acc[key] ?? 0) + Number(record.amount);
      return acc;
    }, {});

    return Object.entries(grouped)
      .sort(([left], [right]) => left.localeCompare(right))
      .map(([month, amount]) => ({
        month: toMonthLabel(new Date(`${month}-01T00:00:00`).getTime()),
        amount,
      }));
  }, [savings]);

  const monthlyComparison = useMemo(() => {
    const grouped = new Map<string, { expenses: number; savings: number }>();

    for (const record of expenses) {
      const key = new Date(record.createdAt).toISOString().slice(0, 7);
      const month = grouped.get(key) ?? { expenses: 0, savings: 0 };
      month.expenses += Number(record.amount);
      grouped.set(key, month);
    }

    for (const record of savings) {
      const key = new Date(record.createdAt).toISOString().slice(0, 7);
      const month = grouped.get(key) ?? { expenses: 0, savings: 0 };
      month.savings += Number(record.amount);
      grouped.set(key, month);
    }

    return Array.from(grouped.entries())
      .sort(([left], [right]) => left.localeCompare(right))
      .map(([month, values]) => ({
        month: toMonthLabel(new Date(`${month}-01T00:00:00`).getTime()),
        expenses: values.expenses,
        savings: values.savings,
      }));
  }, [expenses, savings]);

  const investmentsByInstrument = useMemo(
    () =>
      investments.map((record) => ({
        name: record.instrument.length > 14 ? `${record.instrument.slice(0, 12)}...` : record.instrument,
        amount: Number(record.amount),
      })),
    [investments]
  );

  const goalProgress = useMemo(
    () =>
      goals.map((record) => {
        const target = Number(record.targetAmount);
        const current = Number(record.currentAmount);
        const progress = target > 0 ? Math.min(Math.round((current / target) * 100), 100) : 0;
        return {
          id: record._id,
          name: record.name.length > 14 ? `${record.name.slice(0, 12)}...` : record.name,
          progress,
          currentAmount: current,
          targetAmount: target,
          monthlyIncrement: Number(record.monthlyIncrement),
        };
      }),
    [goals]
  );

  const addExpenseRecord = useCallback(
    async (values: { amount: number; category: string; name?: string; description?: string }) => {
      await addExpense({
        amount: values.amount,
        category: values.category,
        name: values.name || undefined,
        description: values.description || undefined,
        iconKey: pickRandom(EXPENSE_ICON_KEYS),
      });
    },
    [addExpense]
  );

  const addSavingRecord = useCallback(
    async (values: { amount: number; note?: string }) => {
      await addSaving({
        amount: values.amount,
        note: values.note || undefined,
        iconKey: pickRandom(SAVING_ICON_KEYS),
      });
    },
    [addSaving]
  );

  const addInvestmentRecord = useCallback(
    async (values: { amount: number; instrument: string; note?: string }) => {
      await addInvestment({
        amount: values.amount,
        instrument: values.instrument,
        note: values.note || undefined,
        iconKey: pickRandom(INVEST_ICON_KEYS),
      });
    },
    [addInvestment]
  );

  const addGoalRecord = useCallback(
    async (values: { name: string; targetAmount: number; monthlyIncrement: number }) => {
      await addGoal({
        name: values.name,
        targetAmount: values.targetAmount,
        monthlyIncrement: values.monthlyIncrement,
        iconKey: pickRandom(GOAL_ICON_KEYS),
      });
    },
    [addGoal]
  );

  return {
    displayName: formatDisplayName(
      user?.firstName ?? user?.fullName ?? user?.primaryEmailAddress?.emailAddress?.split("@")[0]
    ),
    userEmail: user?.primaryEmailAddress?.emailAddress ?? "",
    expenses,
    savings,
    investments,
    goals,
    totalSavings,
    totalInvestments,
    totalExpenses,
    totalGoalTarget,
    totalGoalSaved,
    monthlyExpenseTotal,
    monthlySavingsTotal,
    expenseByCategory,
    savingsTimeline,
    monthlyComparison,
    investmentsByInstrument,
    goalProgress,
    expenseCount: expenses.length,
    savingsCount: savings.length,
    investmentCount: investments.length,
    goalCount: goals.length,
    addExpenseRecord,
    addSavingRecord,
    addInvestmentRecord,
    addGoalRecord,
    deleteExpenseRecord: (id: Id<"expenses">) => deleteExpense({ id }),
    deleteSavingRecord: (id: Id<"savings">) => deleteSaving({ id }),
    deleteInvestmentRecord: (id: Id<"investments">) => deleteInvestment({ id }),
    deleteGoalRecord: (id: Id<"savingsGoals">) => deleteGoal({ id }),
    incrementGoalRecord: (id: Id<"savingsGoals">) => incrementGoal({ id }),
  };
}
